import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {Message} from 'primeng/message';
import {
  AuditInfo,
  CriteriaWeightingResponse,
  PairwiseComparisonRequest,
  UpsertCriteriaWeightingRequest,
} from '@frontend/shared-api';
import {BadgeListComponent} from '../../../../../../../shared/components/list/badge-list/badge-list.component';
import {BadgeListItem} from '../../../../../../../shared/components/list/badge-list/badge-list.types';
import {ModelFieldSelectInclusionComponent} from '../../../../../../../shared/components/inputs/model-field/model-field-select-inclusion/model-field-select-inclusion.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {LoadingComponent} from '../../../../../../../shared/components/loading/loading.component';
import {SubCriteriaItem} from '../../../../../../../shared/model/sub-criteria.interfaces';
import {
  isEC3,
  isEC8,
  isEC9,
} from '../../../../../../../shared/constants/evaluation-criteria.constants';
import {CriteriaWeightingXComponent} from '../../components/criteria-weighting-x/criteria-weighting-x.component';
import {PairwiseComparisonX, PairwiseItemX} from '../../evaluation-criteria-x.types';
import {CriteriaTabX, SubCriteriaInclusionX} from '../step-4-2-sub-criteria-evaluation-x.types';
import {SubCriteriaInclusionSaveX} from './sub-criteria-tab.types';

const DEFAULT_INCLUDED = true;

@Component({
  selector: 'app-sub-criteria-tab-x',
  imports: [
    TranslocoPipe,
    Message,
    BadgeListComponent,
    ModelFieldSelectInclusionComponent,
    ProjectStepSaveButtonComponent,
    CriteriaWeightingXComponent,
    LoadingComponent,
  ],
  templateUrl: './sub-criteria-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubCriteriaTabXComponent {
  readonly tab = input.required<CriteriaTabX>();
  readonly comparisons = input.required<PairwiseComparisonX[]>();
  readonly weighting = input<CriteriaWeightingResponse | null>(null);
  readonly auditInfo = input<AuditInfo | null>(null);
  readonly disabled = input(false);
  readonly saving = input(false);
  readonly subCriteriaLoading = input(false);

  readonly inclusionsSave = output<SubCriteriaInclusionSaveX>();
  readonly comparisonsSave = output<PairwiseComparisonRequest[]>();
  readonly weightingSave = output<UpsertCriteriaWeightingRequest>();

  private readonly editedInclusions = signal<Map<string, boolean>>(new Map());

  readonly showsInclusionSelection = computed<boolean>(() => {
    const ecNumber = this.tab().ecNumber;

    return !isEC3(ecNumber) && !isEC8(ecNumber) && !isEC9(ecNumber);
  });

  private readonly includesAllSubCriteria = computed<boolean>(() => {
    const ecNumber = this.tab().ecNumber;

    return isEC3(ecNumber) || isEC8(ecNumber);
  });

  private readonly listedSubCriteria = computed<SubCriteriaItem[]>(() => {
    const tab = this.tab();

    return isEC9(tab.ecNumber)
      ? tab.subCriteria.filter((subCriterion) => subCriterion.included === true)
      : tab.subCriteria;
  });

  private readonly unsavedInclusions = computed<Map<string, boolean>>(() => {
    const unsavedInclusions = new Map(this.editedInclusions());

    if (!this.showsInclusionSelection()) {
      return unsavedInclusions;
    }

    for (const subCriterion of this.tab().subCriteria) {
      if (subCriterion.included == null && !unsavedInclusions.has(subCriterion.directusId)) {
        unsavedInclusions.set(subCriterion.directusId, DEFAULT_INCLUDED);
      }
    }

    return unsavedInclusions;
  });

  readonly badgeListItems = computed<BadgeListItem[]>(() => {
    const unsavedInclusions = this.unsavedInclusions();

    return this.listedSubCriteria().map((subCriterion) => ({
      key: subCriterion.directusId,
      identifier: `SC ${subCriterion.scNumber}`,
      translations: subCriterion.translations,
      originalItem: {
        ...subCriterion,
        selectedValue: unsavedInclusions.get(subCriterion.directusId) ?? subCriterion.included,
      },
    }));
  });

  readonly pairwiseItems = computed<PairwiseItemX[]>(() => {
    const includesAllSubCriteria = this.includesAllSubCriteria();

    return this.tab()
      .subCriteria.filter(
        (subCriterion) =>
          subCriterion.id != null && (includesAllSubCriteria || subCriterion.included === true),
      )
      .map((subCriterion) => ({
        id: subCriterion.id ?? 0,
        sortNumber: subCriterion.scNumber,
        identifier: `SC ${subCriterion.scNumber}`,
        translations: subCriterion.translations,
      }));
  });

  readonly showsSaveButton = computed<boolean>(
    () => this.unsavedInclusions().size > 0 && !this.disabled(),
  );

  readonly showsWeightingBlock = computed<boolean>(
    () => !this.subCriteriaLoading() && this.editedInclusions().size === 0,
  );

  readonly showsAllExcludedMessage = computed<boolean>(() => {
    const unsavedInclusions = this.unsavedInclusions();
    const subCriteria = this.tab().subCriteria;

    return (
      this.showsInclusionSelection() &&
      subCriteria.length > 0 &&
      subCriteria.every(
        (subCriterion) =>
          (unsavedInclusions.get(subCriterion.directusId) ?? subCriterion.included) === false,
      )
    );
  });

  constructor() {
    effect(() => {
      this.tab();
      untracked(() => this.editedInclusions.set(new Map()));
    });
  }

  onInclusionChange(directusId: string, included: boolean | null): void {
    if (included === null) return;

    const editedInclusions = new Map(this.editedInclusions());
    editedInclusions.set(directusId, included);
    this.editedInclusions.set(editedInclusions);
  }

  onInclusionsSave(): void {
    const unsavedInclusions = this.unsavedInclusions();
    const tab = this.tab();

    const inclusions: SubCriteriaInclusionX[] = tab.subCriteria
      .filter((subCriterion) => unsavedInclusions.has(subCriterion.directusId))
      .map((subCriterion) => ({
        id: subCriterion.id,
        directusId: subCriterion.directusId,
        scNumber: subCriterion.scNumber,
        included: unsavedInclusions.get(subCriterion.directusId) === true,
      }));

    this.inclusionsSave.emit({ecNumber: tab.ecNumber, inclusions});
  }

  onComparisonsSave(comparisons: PairwiseComparisonRequest[]): void {
    this.comparisonsSave.emit(comparisons);
  }

  onWeightingSave(request: UpsertCriteriaWeightingRequest): void {
    this.weightingSave.emit(request);
  }
}
